import React, { useState, useEffect } from "react";
import { XMarkIcon, CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ImageWithFallback } from "../../../Utils/loader";

function AddCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
}) {
  const [categoryData, setCategoryData] = useState({
    name: "",
    image: null,
    status: true,
  });

  const [nameError, setNameError] = useState("");
  const [imageError, setImageError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (category) {
      setCategoryData({
        name: category.name,
        image: category.image,
        status: category.status,
      });
      if (category.image) {
        setImagePreview(category.image);
      }
    } else {
      setCategoryData({
        name: "",
        image: null,
        status: true,
      });
      setImagePreview(null);
    }
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prevData) => ({
      ...prevData,
      [name]: value === "true" ? true : value === "false" ? false : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCategoryData((prevData) => ({
        ...prevData,
        image: file,
      }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setCategoryData((prevData) => ({
      ...prevData,
      image: null,
    }));
    setImagePreview(null);
    setImageError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let formValid = true;

    setNameError("");
    setImageError("");

    if (!categoryData.name) {
      setNameError("Category name is required.");
      formValid = false;
    }

    if (!categoryData.image) {
      setImageError("Image is required.");
      formValid = false;
    }

    if (!formValid) {
      return;
    }

    handleSaveCategory(categoryData, category ? category.id : null);

    setCategoryData({
      name: "",
      image: null,
      status: true,
    });
    setImagePreview(null);

    toggleModal();
  };

  const handleCloseModal = () => {
    setCategoryData({
      name: "",
      image: null,
      status: true,
    });
    setImagePreview(null);
    setNameError("");
    setImageError("");
    toggleModal();
  };

  const isFormValid = categoryData.name.trim().length >= 2 && categoryData.image;

  return (
    modalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-8 rounded-2xl w-[500px] max-h-[90vh] overflow-y-auto relative">
          <XMarkIcon
            className="absolute top-4 right-4 h-6 w-6 text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
            onClick={handleCloseModal}
          />
          <h3 className="text-2xl font-semibold mb-6 text-center text-gray-800">
            {category ? "Edit Category" : "Add Category"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload Section */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Category Image <span className="text-red-500">*</span>
              </label>

              {imagePreview ? (
                <div className="relative">
                  <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    <ImageWithFallback
                      src={imagePreview}
                      alt="Category preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                    title="Remove image"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer w-full h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
                  <span className="text-gray-600 font-medium">Upload Image</span>
                  <span className="text-gray-400 text-sm mt-1">Click to browse files</span>
                </label>
              )}

              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />

              {imageError && (
                <p className="text-red-500 text-sm">{imageError}</p>
              )}
              <p className="text-gray-500 text-xs">
                Image is required for category creation
              </p>
            </div>

            {/* Category Name Input */}
            <div className="space-y-2">
              <label htmlFor="category-name" className="block text-sm font-medium text-gray-700">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                id="category-name"
                type="text"
                name="name"
                value={categoryData.name}
                onChange={handleInputChange}
                placeholder="Enter category name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              />
              {nameError && (
                <p className="text-red-500 text-sm">{nameError}</p>
              )}
              <p className="text-gray-500 text-xs">
                Minimum 2 characters required
              </p>
            </div>

            {/* Status Select */}
            <div className="space-y-2">
              <label htmlFor="category-status" className="block text-sm font-medium text-gray-700">
                Status <span className="text-gray-400">(Optional)</span>
              </label>
              <select
                id="category-status"
                name="status"
                value={categoryData.status}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </select>
              <p className="text-gray-500 text-xs">
                Default status is Active
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isFormValid}
              className={`w-full font-medium py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-offset-2 ${isFormValid
                ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
              {category ? "Update Category" : "Create Category"}
            </button>
          </form>
        </div>
      </div>
    )
  );
}

export default AddCategoryModal;
